import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import dayjs from "dayjs";
interface Order {
  shipping_address: string;
  shipping_name: string;
  grand_total: number;
  order_id: number;
  status: string;
  order_number: string;
  order_date: string | Date;
  order_status: string;
  spare_part_name: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  address_type: string;
  state_name: string;
  city_name: string;
  region_name: string;
  sku: string;
  quantity: number;
  unit_price: number;
  sub_total: number;
  gst_amount: number;
  total_price: number;
  billing_name: string;
  billing_address: string;
  billing_city: string;
  billing_phone: string;
  billing_email: string;
  cancelled_at: string | Date;
  delivered_at: string | Date;
  shipped_at: string | Date;
}
export const generateInvoicePDF = (order: Order) => {
  const doc = new jsPDF();

  // --- HEADER ---
  doc.setFontSize(18);
  doc.setFont("bold");
  doc.text("INVOICE", 105, 20, { align: "center" });

  doc.setFontSize(11);
  doc.setFont("normal");
  doc.text(`Order Number: ${order?.order_number || "N/A"}`, 14, 35);
  doc.text(
    `Order Date: ${dayjs(order?.order_date).format(
      "MMM DD, YYYY [at] hh:mm A"
    )}`,
    14,
    42
  );

  // --- CUSTOMER INFO ----
  doc.setFontSize(13);
  doc.setFont("bold");
  doc.text("Customer Details", 14, 55);

  doc.setFontSize(10);
  doc.setFont("normal");
  doc.text(`Name: ${order?.customer_name || "-"}`, 14, 62);
  doc.text(`Email: ${order?.customer_email || "-"}`, 14, 68);
  doc.text(`Phone: ${order?.customer_phone || "-"}`, 14, 74);

  const addressLine = order?.customer_address || "-";
  const addressType = order?.address_type ? ` (${order.address_type})` : "";
  const city = order?.city_name ? `, ${order.city_name}` : "";
  const state = order?.state_name ? `, ${order.state_name}` : "";
  const region = order?.region_name ? `, ${order.region_name}` : "";

  doc.text(
    `Address: ${addressLine}${addressType}${city}${state}${region}`,
    14,
    80,
    { maxWidth: 180 }
  );

  // --- ORDER INFO ---
  doc.setFontSize(13);
  doc.setFont("bold");
  doc.text("Order Details", 14, 95);

  const formatCurrency = (value: number) => {
    const num = Number(value || 0);
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const tableData = [
    [
      order?.spare_part_name || "-",
      order?.sku || "-",
      String(order?.quantity || 0),
      formatCurrency(order?.unit_price),
      formatCurrency(order?.total_price),
    ],
  ];
  //@ts-ignore
  autoTable(doc, {
    head: [["Product", "SKU", "Quantity", "Unit Price (Rs.)", "Total (Rs.)"]],
    body: tableData,
    startY: 100,
    styles: {
      fontSize: 10,
      cellPadding: 4,
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [0, 128, 128],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "left" },
      2: { halign: "left", cellWidth: 25 },
      3: { halign: "left", cellWidth: 35 },
      4: { halign: "left", cellWidth: 35 },
    },
  });

  // --- TOTALS SECTION ---
  const finalY = doc.lastAutoTable?.finalY || 110;

  // Draw a line before totals
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(120, finalY + 8, 196, finalY + 8);

  // Calculate and format values
  const subtotal = formatCurrency(order?.sub_total || order?.total_price || 0);
  const gst = formatCurrency(order?.gst_amount || 0);
  const grandTotal = formatCurrency(
    order?.grand_total || order?.total_price || 0
  );

  // Subtotal row
  doc.setFontSize(11);
  doc.setFont("normal");
  doc.setTextColor(0, 0, 0);
  doc.text("Subtotal:", 140, finalY + 18);
  doc.text("Rs. " + subtotal, 196, finalY + 18, { align: "right" });

  // GST row
  doc.setFontSize(11);
  doc.setFont("normal");
  doc.text("GST:", 140, finalY + 25);
  doc.text("Rs. " + gst, 196, finalY + 25, { align: "right" });

  // Grand Total row (bold and slightly larger)
  doc.setFontSize(12);
  doc.setFont("bold");
  doc.text("Grand Total:", 140, finalY + 35);
  doc.text("Rs. " + grandTotal, 196, finalY + 35, { align: "right" });

  // --- BILLING & SHIPPING ADDRESSES ---
  let yStart = finalY + 45;

  doc.setFontSize(13);
  doc.setFont("bold");
  doc.text("Billing & Shipping Information", 14, yStart);

  doc.setFontSize(10);
  doc.setFont("normal");

  // Billing
  doc.text("Billing Address:", 14, yStart + 8);
  doc.text(`${order?.customer_name || "-"}`, 14, yStart + 14);
  doc.text(`${order?.customer_address || "-"}`, 14, yStart + 20, {
    maxWidth: 80,
  });
  doc.text(
    `${order?.city_name || ""}, ${order?.state_name || ""}`,
    14,
    yStart + 26
  );
  doc.text(`${order?.customer_phone || "-"}`, 14, yStart + 32);
  doc.text(`${order?.customer_email || "-"}`, 14, yStart + 38);

  // Shipping
  doc.text("Shipping Address:", 110, yStart + 8);
  doc.text(
    `${order?.shipping_name || order?.customer_name || "-"}`,
    110,
    yStart + 14
  );
  doc.text(
    `${order?.shipping_address || order?.customer_address || "-"}`,
    110,
    yStart + 20,
    { maxWidth: 80 }
  );
  doc.text(
    `${order?.city_name || ""}, ${order?.state_name || ""}`,
    110,
    yStart + 26
  );
  doc.text(`${order?.customer_phone || "-"}`, 110, yStart + 32);
  doc.text(`${order?.customer_email || "-"}`, 110, yStart + 38);

  // --- FOOTER ---
  doc.setFontSize(10);
  doc.setFont("italic");
  doc.setTextColor(100, 100, 100);
  doc.text("Thank you for your order!", 105, 280, { align: "center" });

  // --- SAVE ---
  doc.save(`${order?.order_number || "Invoice"}.pdf`);
};
